/**
 * DisciplineScorePanel.tsx — MER Algo Footprints v7.1B — Phase 7.1B
 *
 * features/71b/DisciplineScorePanel.tsx — Phase 7.1B
 *
 * Read-only operator discipline scoring.
 * Deterministic, explicit, rule-based — never affects scanner/ranking/setup logic.
 * Score applies to the operator's workflow behavior, not the engine.
 *
 * Scoring dimensions:
 *   + Setup reviewed before action        (+5 per entry with note or outcome)
 *   + Note added                          (+3 per note_added entry)
 *   + Outcome recorded                    (+4 per outcome_reviewed entry)
 *   + Skipped low-readiness setup         (+2 per skipped entry with score < 45)
 *   − Took low-score setup without note   (−3 per trade_taken with score < 45 + no note)
 *   − Missing outcome closure             (−1 per taken entry with no outcome review for same symbol/setup)
 *
 * Maximum score is capped at 100. Score = 0 when no entries.
 * All rules are shown explicitly.
 */

import React, { useMemo, useState } from 'react';
import { useStore } from '../../store';
import { Journal71AEntry } from '../../types';
import { exportDisciplineJSON, DisciplineExportPayload } from './export71b';

// ─── Rule definitions ─────────────────────────────────────────────────────────

interface DisciplineRule {
  id:      string;
  label:   string;
  detail:  string;
  value:   number;   // points per trigger (positive = good, negative = concern)
  count:   number;   // how many times triggered
  total:   number;   // value * count
  color:   string;
}

interface DisciplineResult {
  rawScore:    number;
  cappedScore: number;
  grade:       'Elite' | 'Strong' | 'Developing' | 'Needs Work' | 'No Data';
  rules:       DisciplineRule[];
  observations: string[];
}

// ─── Score computation ────────────────────────────────────────────────────────

function computeDisciplineScore(entries: Journal71AEntry[]): DisciplineResult {
  if (!entries.length) {
    return {
      rawScore: 0, cappedScore: 0, grade: 'No Data', rules: [], observations: [],
    };
  }

  // R1: Note added (+3 each)
  const r1Count = entries.filter(e => e.actionType === 'note_added').length;

  // R2: Outcome recorded (+4 each)
  const r2Count = entries.filter(e => e.actionType === 'outcome_reviewed').length;

  // R3: Skipped low-score setup — operator showed discipline (+2 each, score < 45)
  const r3Count = entries.filter(e => e.actionType === 'trade_skipped' && (e.score ?? 100) < 45).length;

  // R4: Reviewed before action — viewed before taken/skipped same symbol (check for setup_viewed near action)
  // Approximation: any setup_viewed entry gets +2 each (proxy for review before action)
  const r4Count = entries.filter(e => e.actionType === 'setup_viewed').length;

  // P1: Took low-score setup without note (−3 each)
  const p1Count = entries.filter(e =>
    e.actionType === 'trade_taken' && (e.score ?? 100) < 45 && !e.note
  ).length;

  // P2: Took high-score setup without outcome review (−1 each, proxy: taken with score >= 65, no paired outcome)
  const takenHighScore = entries.filter(e => e.actionType === 'trade_taken' && (e.score ?? 0) >= 65);
  const outcomeSymbols = new Set(
    entries.filter(e => e.actionType === 'outcome_reviewed').map(e => e.symbol)
  );
  const p2Count = takenHighScore.filter(e => !outcomeSymbols.has(e.symbol)).length;

  const rules: DisciplineRule[] = [
    { id: 'R1', label: 'Note added',              detail: 'Adding context notes to setups',         value: 3,  count: r1Count, total: r1Count * 3,  color: 'var(--cyan)'   },
    { id: 'R2', label: 'Outcome recorded',         detail: 'Reviewing and recording trade outcomes', value: 4,  count: r2Count, total: r2Count * 4,  color: 'var(--purple)' },
    { id: 'R3', label: 'Skipped weak setup',       detail: 'Passing on low-score (<45) setups',      value: 2,  count: r3Count, total: r3Count * 2,  color: 'var(--green)'  },
    { id: 'R4', label: 'Reviewed before action',   detail: 'Opening attribution before trading',     value: 2,  count: r4Count, total: r4Count * 2,  color: 'var(--blue)'   },
    { id: 'P1', label: 'Took weak setup unchecked',detail: 'Took low-score (<45) setup, no note',    value: -3, count: p1Count, total: p1Count * -3, color: 'var(--red)'    },
    { id: 'P2', label: 'Missing outcome review',   detail: 'Took high-score setup, no outcome logged',value: -1,count: p2Count, total: p2Count * -1, color: 'var(--amber)'  },
  ];

  const rawScore    = rules.reduce((s, r) => s + r.total, 0);
  const cappedScore = Math.min(100, Math.max(0, rawScore));
  const grade: DisciplineResult['grade'] =
    cappedScore >= 80 ? 'Elite'
    : cappedScore >= 60 ? 'Strong'
    : cappedScore >= 35 ? 'Developing'
    : 'Needs Work';

  // Observations
  const obs: string[] = [];
  if (r2Count === 0 && entries.filter(e => e.actionType === 'trade_taken').length > 0) {
    obs.push('No outcomes recorded for taken trades — add outcome reviews to track performance.');
  }
  if (p1Count > 1) {
    obs.push(`Took ${p1Count} low-score setups without notes — consider documenting the rationale.`);
  }
  if (r1Count > 0 && r2Count === 0) {
    obs.push('Good note habit — next step is recording outcomes to close the loop.');
  }
  if (cappedScore >= 70) {
    obs.push('Strong discipline pattern — consistent review and documentation.');
  }

  return { rawScore, cappedScore, grade, rules, observations: obs };
}

// ─── Grade display ────────────────────────────────────────────────────────────

const GRADE_COLOR: Record<string, string> = {
  Elite:      'var(--gold)',
  Strong:     'var(--green)',
  Developing: 'var(--amber)',
  'Needs Work':'var(--red)',
  'No Data':  'var(--t3)',
};

// ─── Score gauge ─────────────────────────────────────────────────────────────

function ScoreGauge({ score }: { score: number }) {
  const color = score >= 70 ? 'var(--green)' : score >= 40 ? 'var(--amber)' : 'var(--red)';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '10px 0 14px' }}>
      <div style={{ fontSize: 42, fontWeight: 700, color, fontFamily: 'var(--mono)', lineHeight: 1 }}>
        {score}
      </div>
      <div style={{ fontSize: 9, color: 'var(--t3)', marginTop: 4 }}>/ 100</div>
      <div style={{
        width: '100%', height: 6, background: 'var(--surface)',
        borderRadius: 3, overflow: 'hidden', marginTop: 10,
      }}>
        <div style={{
          width: `${score}%`, height: '100%', background: color, borderRadius: 3, transition: 'width .3s',
        }} />
      </div>
    </div>
  );
}

// ─── Main panel ───────────────────────────────────────────────────────────────

export function DisciplineScorePanel() {
  const entries = useStore(s => s.journal71aEntries);
  const result  = useMemo(() => computeDisciplineScore(entries), [entries]);
  const [showMethod, setShowMethod] = useState(false);

  if (!entries.length) {
    return (
      <div style={{ padding: '28px 0', color: 'var(--t3)', textAlign: 'center' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--t2)', marginBottom: 8 }}>
          🎯 Execution Discipline
        </div>
        <div style={{ fontSize: 11 }}>Log activity to compute your discipline score.</div>
      </div>
    );
  }

  const gradeColor = GRADE_COLOR[result.grade];

  return (
    <div>
      {/* Score + grade + action bar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)',
          textTransform: 'uppercase', letterSpacing: '.8px' }}>
          🎯 Execution Discipline
        </div>
        <div style={{ display: 'flex', gap: 5 }}>
          <button
            onClick={() => setShowMethod(p => !p)}
            style={{
              fontSize: 8, fontFamily: 'var(--mono)', fontWeight: 700,
              color: showMethod ? 'var(--cyan)' : 'var(--blue-dim)',
              border: `1px solid ${showMethod ? 'var(--cyan)44' : 'var(--border)'}`,
              background: showMethod ? 'var(--cyan)10' : 'none',
              padding: '2px 7px', borderRadius: 3, cursor: 'pointer',
            }}>
            {showMethod ? '▲ methodology' : '▼ methodology'}
          </button>
          <button
            onClick={() => exportDisciplineJSON({
              exportedAt: new Date().toISOString(),
              version: '7.1B',
              source: 'MER Algo Footprints — Discipline Score',
              cappedScore: result.cappedScore,
              rawScore: result.rawScore,
              grade: result.grade,
              entryCount: entries.length,
              rules: result.rules.map(r => ({
                id: r.id, label: r.label, detail: r.detail,
                value: r.value, count: r.count, total: r.total,
              })),
              observations: result.observations,
            } as DisciplineExportPayload)}
            style={{
              fontSize: 8, fontFamily: 'var(--mono)', fontWeight: 700,
              color: 'var(--blue-dim)', border: '1px solid var(--border)',
              background: 'none', padding: '2px 7px', borderRadius: 3, cursor: 'pointer',
            }}>⬇ JSON</button>
        </div>
      </div>

      {/* Methodology collapsible */}
      {showMethod && (
        <div style={{
          marginBottom: 12, padding: '8px 10px',
          background: 'var(--surface)', border: '1px solid var(--cyan)33',
          borderLeft: '3px solid var(--cyan)', borderRadius: '0 4px 4px 0',
          fontSize: 9, color: 'var(--blue-muted)', lineHeight: 1.6,
        }}>
          <div style={{ fontWeight: 700, color: 'var(--cyan)', marginBottom: 5,
            fontSize: 8, letterSpacing: '.5px', textTransform: 'uppercase' }}>
            Score Methodology
          </div>
          <div>Score = sum of rule contributions, capped at 0–100.</div>
          <div style={{ marginTop: 4, display: 'grid', gridTemplateColumns: '18px 1fr', gap: '2px 6px' }}>
            <span style={{ color: 'var(--green)' }}>+</span><span>Note added per entry (+3)</span>
            <span style={{ color: 'var(--green)' }}>+</span><span>Outcome recorded per entry (+4)</span>
            <span style={{ color: 'var(--green)' }}>+</span><span>Skipped low-score setup (&lt;45) (+2)</span>
            <span style={{ color: 'var(--green)' }}>+</span><span>Reviewed before acting (+5)</span>
            <span style={{ color: 'var(--red)' }}>−</span><span>Took low-score setup without note (−3)</span>
            <span style={{ color: 'var(--red)' }}>−</span><span>Missing outcome for taken trade (−1)</span>
          </div>
          <div style={{ marginTop: 5, color: 'var(--t3)', fontSize: 8, fontStyle: 'italic' }}>
            This score is informational only. It does not affect scanner output, ranking, or any engine decision.
          </div>
        </div>
      )}

      {/* Score + grade */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14,
      }}>
        <div style={{
          background: 'var(--surface2)', border: '1px solid var(--border)',
          borderRadius: 4, padding: '10px 14px',
        }}>
          <div style={{ fontSize: 9, color: 'var(--t3)', textTransform: 'uppercase',
            letterSpacing: '.8px', marginBottom: 2 }}>Discipline Score</div>
          <ScoreGauge score={result.cappedScore} />
        </div>
        <div style={{
          background: 'var(--surface2)', border: `1px solid ${gradeColor}44`,
          borderRadius: 4, padding: '10px 14px', textAlign: 'center',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{ fontSize: 9, color: 'var(--t3)', textTransform: 'uppercase',
            letterSpacing: '.8px', marginBottom: 6 }}>Grade</div>
          <div style={{ fontSize: 26, fontWeight: 700, color: gradeColor, fontFamily: 'var(--mono)' }}>
            {result.grade}
          </div>
          <div style={{ fontSize: 8, color: 'var(--t3)', marginTop: 4 }}>
            Based on {entries.length} logged entries
          </div>
        </div>
      </div>

      {/* Rule breakdown */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)',
          letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 7 }}>
          Rule Breakdown
        </div>
        {result.rules.filter(r => r.count > 0 || r.value < 0).map(r => (
          <div key={r.id} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '5px 0', borderBottom: '1px solid var(--surface)',
          }}>
            <span style={{
              fontSize: 8, fontFamily: 'var(--mono)', fontWeight: 700,
              color: r.color, border: `1px solid ${r.color}44`,
              background: `${r.color}10`, padding: '1px 5px', borderRadius: 3,
              minWidth: 22, textAlign: 'center',
            }}>{r.id}</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: 'var(--text)' }}>{r.label}</div>
              <div style={{ fontSize: 8, color: 'var(--t3)' }}>{r.detail}</div>
            </div>
            <span style={{ fontSize: 9, fontFamily: 'var(--mono)', color: 'var(--blue-dim)', minWidth: 24, textAlign: 'right' }}>
              ×{r.count}
            </span>
            <span style={{
              fontSize: 10, fontFamily: 'var(--mono)', fontWeight: 700,
              color: r.total >= 0 ? 'var(--green)' : 'var(--red)',
              minWidth: 32, textAlign: 'right',
            }}>
              {r.total >= 0 ? '+' : ''}{r.total}
            </span>
          </div>
        ))}
        <div style={{
          display: 'flex', justifyContent: 'flex-end', paddingTop: 6,
          fontSize: 10, fontFamily: 'var(--mono)', gap: 8,
        }}>
          <span style={{ color: 'var(--t3)' }}>Raw total:</span>
          <span style={{ color: result.rawScore >= 0 ? 'var(--green)' : 'var(--red)', fontWeight: 700 }}>
            {result.rawScore >= 0 ? '+' : ''}{result.rawScore}
          </span>
          <span style={{ color: 'var(--t3)' }}>→ capped:</span>
          <span style={{ color: 'var(--gold)', fontWeight: 700 }}>{result.cappedScore}</span>
        </div>
      </div>

      {/* Observations */}
      {result.observations.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <div style={{ fontSize: 9, color: 'var(--t3)', fontFamily: 'var(--mono)',
            letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 6 }}>
            Observations
          </div>
          {result.observations.map((obs, i) => (
            <div key={i} style={{
              fontSize: 10, color: 'var(--blue-muted)', lineHeight: 1.5,
              padding: '4px 0', borderBottom: '1px solid var(--surface)',
            }}>
              · {obs}
            </div>
          ))}
        </div>
      )}

      {/* Disclaimer */}
      <div style={{ fontSize: 8, color: 'var(--t3)', marginTop: 12, lineHeight: 1.5, fontStyle: 'italic' }}>
        This score reflects operator workflow habits only. It has no effect on scanner rankings,
        setup generation, or any engine output.
      </div>
    </div>
  );
}
